import { query } from "@/lib/shopify";
import {
  clearVoucherSession,
  removeVoucherFromSession,
  type AppliedVoucher,
} from "@/shared/api/activeKids";
import { CART_ATTRIBUTES_UPDATE } from "@/lib/shopify/queries";

/**
 * The Active Kids cart attribute, and the operations that have to keep it in step with
 * the server-side session.
 *
 * The attribute is the durable record of an in-progress voucher: it survives a page
 * reload, which React context does not, and it is what the discount Function reads.
 * Written when a voucher is validated and rewritten whenever the balance changes - the
 * Function already has the lines and the prices, so the only thing it cannot derive is
 * the balance.
 *
 * Removing and discarding live here rather than in the API module because the ordering
 * between the cart write and the session call is the part that matters, and getting it
 * wrong leaves a cart discounting against a voucher nobody holds.
 */

export const ACK_CART_ATTRIBUTE = "ack_session";
/** Base64-encoded on write, matching `_visitorLocationInfo`, and decoded by next-app. */
export const ACK_LINE_PROPERTY = "_ack_session_id";

/** Signed server-side. The widget passes it through without inspecting the signature. */
export type AckSessionPayload = {
  v: number;
  sessionId: string;
  balanceCents: number;
  exp: number;
  expDate: string;
  hmac: string;
};

const isHeadless = (productRenderedLocation: string) => productRenderedLocation === "external";

export async function writeAckCartAttribute(
  payload: AckSessionPayload,
  options: { productRenderedLocation: string; headlessCartId?: string },
): Promise<void> {
  const value = JSON.stringify(payload);

  if (isHeadless(options.productRenderedLocation)) {
    if (!options.headlessCartId) throw new Error("headlessCartId is required for a headless cart");
    await query(CART_ATTRIBUTES_UPDATE, {
      cartId: options.headlessCartId,
      attributes: [{ key: ACK_CART_ATTRIBUTE, value }],
    });
    return;
  }

  // Ajax carts take attributes as an object, and omitted keys are left untouched.
  const response = await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attributes: { [ACK_CART_ATTRIBUTE]: value } }),
  });
  if (!response.ok) {
    throw new Error("Cart update failed: " + response.statusText);
  }
}

/** Passing an empty string is how Ajax carts delete an attribute; null does nothing. */
export async function clearAckCartAttribute(options: {
  productRenderedLocation: string;
  headlessCartId?: string;
}): Promise<void> {
  if (isHeadless(options.productRenderedLocation)) {
    if (!options.headlessCartId) return;
    await query(CART_ATTRIBUTES_UPDATE, {
      cartId: options.headlessCartId,
      attributes: [{ key: ACK_CART_ATTRIBUTE, value: "" }],
    });
    return;
  }

  await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attributes: { [ACK_CART_ATTRIBUTE]: "" } }),
  });
}

function parsePayload(value: unknown): AckSessionPayload | null {
  if (typeof value !== "string" || !value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.sessionId !== "string" || !parsed.sessionId) return null;
    if (typeof parsed.balanceCents !== "number") return null;
    return parsed as AckSessionPayload;
  } catch {
    return null;
  }
}

/**
 * Recover an in-progress voucher from the cart after a reload.
 *
 * Without this a customer who refreshes sees an unticked checkbox and no voucher line
 * while the cart still carries a live allocation - and re-entering the same code would
 * collide with the reservation their own abandoned session still holds.
 *
 * Accepts either cart shape: `attributes` is an object on Ajax carts and an array of
 * key/value pairs on the Storefront API.
 */
export function readAckCartAttribute(cart: unknown): AckSessionPayload | null {
  if (!cart || typeof cart !== "object") return null;
  const attributes = (cart as { attributes?: unknown }).attributes;
  if (!attributes) return null;

  if (Array.isArray(attributes)) {
    const found = attributes.find(
      (attribute) => (attribute as { key?: string })?.key === ACK_CART_ATTRIBUTE,
    );
    return parsePayload((found as { value?: unknown })?.value);
  }

  return parsePayload((attributes as Record<string, unknown>)[ACK_CART_ATTRIBUTE]);
}

/** True once the session behind the attribute has lapsed and the discount has stopped. */
export function isAckPayloadExpired(payload: AckSessionPayload, nowMs = Date.now()): boolean {
  return payload.exp * 1000 <= nowMs;
}

const DISCARD_TIMEOUT_MS = 5000;

function withTimeout<T>(work: Promise<T>): Promise<T> {
  return Promise.race([
    work,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Timed out")), DISCARD_TIMEOUT_MS),
    ),
  ]);
}

/**
 * Give up an in-progress voucher: forget it in the cart, then release the reservation.
 *
 * The order is load-bearing. A cart still pointing at a released session keeps the
 * discount applying with nothing behind it to redeem, and settlement answers that by
 * cancelling the order. Clearing the attribute first means the worst case is an orphaned
 * session, which expires on its own.
 *
 * Best effort by design: the customer asked to move on, so a failure here must not stop
 * them. Reconciliation at `orders/paid` is the real guarantee.
 */
export async function discardAckSession(options: {
  sessionId: string;
  productRenderedLocation: string;
  headlessCartId?: string;
}): Promise<boolean> {
  try {
    // Bounded, because the caller navigates on the result: a request that never settles
    // would leave the customer watching a spinner, which is the one outcome "best effort"
    // is supposed to rule out.
    await withTimeout(clearAckCartAttribute(options));
    await withTimeout(clearVoucherSession(options.sessionId));
    return true;
  } catch (error) {
    console.error("Failed to discard the Active Kids session:", error);
    return false;
  }
}

/**
 * Take one voucher back off the session and leave the cart consistent with what remains.
 *
 * The server re-signs the payload for the reduced balance, so the cart is rewritten
 * rather than patched - the old payload would let the Function discount against a
 * voucher that is no longer held. `cleared` means nothing is left and the attribute goes.
 *
 * Unlike a discard this is not best effort: the customer is looking at the number change,
 * so a failure has to surface.
 */
export async function removeAckVoucher(options: {
  sessionId: string;
  code: string;
  productRenderedLocation: string;
  headlessCartId?: string;
}): Promise<
  { cleared: true } | { cleared: false; totalBalanceCents: number; vouchers: AppliedVoucher[] }
> {
  const result = await removeVoucherFromSession(options.sessionId, options.code);

  if (result.cleared) {
    await clearAckCartAttribute(options);
    return { cleared: true };
  }

  await writeAckCartAttribute(result.payload, options);
  return {
    cleared: false,
    totalBalanceCents: result.totalBalanceCents,
    vouchers: result.vouchers,
  };
}
